// The gantt's right-rail panel (TODO §26/§8).
//
// Two halves, and the split is deliberate:
//
//   • **The task editor is SVAR's own `<Editor>`**, handed the live api. It already edits
//     every field the task model has — text, dates, duration, progress, type, links — and
//     rebuilding that by hand against `api.exec("update-task", …)` would be the largest piece
//     of work in the whole feature for a worse result. It renders inside a `<Willow>` of our
//     own, so the theme bridge in `src/styles/gantt.css` reaches it too.
//   • **The view controls are ours**, because they are not the library's concern: unit, fit,
//     the task-list column and the dependency arrows are how the chart sits ON A SLIDE, and
//     they live on our doc (`GanttView`), not in SVAR's store.
//
// Lazily loaded through the same door as the block (§7.1): opening this panel with no gantt
// in the deck must not pull the chunk.

import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Diamond, Plus } from "lucide-react";

import { useGanttEditing } from "@/components/deck/ganttEditing";
// §15/§7.5 Moved out of this file when the Chart style panel needed the same picker.
import { SwatchRow } from "@/components/deck/SwatchRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { newTaskDates, swatchValue } from "@/lib/gantt";
import { loadGanttCanvas } from "@/lib/gantt-engine";
import { ganttDocUpdated } from "@/store/ganttSlice";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type { GanttTask, GanttView } from "@/types/gantt";

const GanttTaskEditor = lazy(() =>
  loadGanttCanvas().then((m) => ({ default: m.GanttTaskEditor })),
);

const UNITS: { value: GanttView["unit"]; label: string }[] = [
  { value: "day", label: "Days" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
  { value: "quarter", label: "Quarters" },
];

export function GanttPanel() {
  const dispatch = useAppDispatch();
  const { activeDocId, api } = useGanttEditing();
  const doc = useAppSelector((s) =>
    activeDocId ? s.gantt[activeDocId] : undefined,
  );
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  const swatches = useMemo(
    () => sheetSwatches(effectiveStyleSheet),
    [effectiveStyleSheet],
  );

  // §6.3 Which task the chart has selected, so the per-task colours can target it. Tracked
  // through the api's own event rather than through Redux: selection is the widget's state,
  // it changes on every click, and it is none of the document's business.
  //
  // `detach` on a TAG is the only unsubscribe SVAR offers, so every handler this panel adds
  // carries one — without it, re-opening the panel would stack a second listener on the same
  // api, and a fifth, and a tenth.
  const [selectedId, setSelectedId] = useState<GanttTask["id"] | null>(null);
  useEffect(() => {
    if (!api) {
      setSelectedId(null);
      return;
    }
    const state = api.getState() as { _selected?: { id?: GanttTask["id"] }[] };
    setSelectedId(state._selected?.[0]?.id ?? null);
    api.on(
      "select-task",
      ({ id }: { id: GanttTask["id"] }) => setSelectedId(id),
      {
        tag: "scs-gantt-panel",
      },
    );
    return () => {
      api.detach("scs-gantt-panel");
      // §26/§8.5 Tell the widget its editor is gone. `activeTask` is the widget's own "an
      // editor is showing" flag, and while it is set, `select-task` re-execs `show-editor` on
      // every click that lands on a DIFFERENT task — which `GanttBlock` now turns into "open
      // the Gantt panel". Leaving it set would make a closed panel spring back open on the
      // next single click, i.e. a panel that will not stay shut. Clearing it here means only a
      // fresh double click opens it again, which is the gesture that opened it in the first
      // place.
      // `null` IS the close value — the widget execs exactly this itself when its own editor
      // closes — but the published types declare `id: TID` with no null in it, and `TID` is
      // not re-exported by `@svar-ui/react-gantt`. So the call is made through a structural
      // signature: the cast is against the type declaration, not against the behaviour.
      (api.exec as unknown as (event: string, data: unknown) => void)(
        "show-editor",
        { id: null },
      );
    };
  }, [api]);

  const selectedTask = useMemo(() => {
    if (!api || selectedId === null) return null;
    return (api.getTask(selectedId) ?? null) as GanttTask | null;
  }, [api, selectedId]);

  // The same choice §21/§8.1 made for the diagram panel: a rail button that vanishes when
  // nothing is selected is worse than one that is always there and says why it is empty.
  if (!activeDocId || !doc) {
    return (
      <div className="h-full overflow-y-auto p-4 text-sm text-slate-500">
        Add a gantt to a slide, or click into one, to edit its tasks here.
      </div>
    );
  }

  // §8.3 Adding a task goes through the LIVE widget (`api.exec`), never through the stored
  // doc: the widget is the one holding the scene, and a task written straight into Redux would
  // be overwritten by its next autosave. The exec dispatches `add-task`, which our own
  // listener in `GanttCanvas` already persists — so there is no second save path here.
  //
  // The chart's right-click menu offers the same action plus above/below/child placement; this
  // button exists because a context menu is not something anyone discovers on a slide.
  const addTask = (type: "task" | "milestone") => {
    if (!api) return;
    void api.exec("add-task", {
      task: {
        text: type === "milestone" ? "New milestone" : "New task",
        type,
        ...newTaskDates(doc, type),
      },
      select: true,
    });
  };

  // §6.3 A per-task colour goes through the WIDGET, like adding a task does: the field rides
  // along on SVAR's own task record, comes back out through `serialize()` and is persisted by
  // the same autosave. Written into Redux directly it would be erased by the next save.
  //
  // The patch is typed to the colour fields alone rather than to `Partial<GanttTask>`: our
  // task type carries ISO date STRINGS and the library's carries `Date`s (§3.2.2), so the two
  // are deliberately not assignable to each other. Naming the two fields is both narrower and
  // more honest than a cast across that boundary.
  const setTaskColor = (patch: {
    colorName?: string;
    doneColorName?: string;
  }) => {
    if (!api || selectedId === null) return;
    void api.exec("update-task", { id: selectedId, task: patch });
  };

  const setView = (patch: Partial<GanttView>) => {
    dispatch(
      ganttDocUpdated({
        docId: activeDocId,
        doc: { ...doc, view: { ...doc.view, ...patch } },
      }),
    );
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 border-b border-slate-200 p-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Tasks
          </Label>
          <div className="flex flex-wrap gap-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={!api}
              onClick={() => addTask("task")}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add task
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!api}
              onClick={() => addTask("milestone")}
            >
              <Diamond className="mr-1 h-3.5 w-3.5" /> Add milestone
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Right-click a bar in the chart to add one above, below or inside it,
            or to delete it.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Timeline
          </Label>
          <div className="flex flex-wrap gap-1">
            {UNITS.map((unit) => (
              <Button
                key={unit.value}
                variant={doc.view.unit === unit.value ? "default" : "secondary"}
                size="sm"
                onClick={() => setView({ unit: unit.value })}
              >
                {unit.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="gantt-fit" className="text-sm text-slate-700">
            Fit to window
          </Label>
          <Switch
            id="gantt-fit"
            checked={doc.view.fit === "window"}
            onCheckedChange={(on) => setView({ fit: on ? "window" : "fixed" })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="gantt-tasklist" className="text-sm text-slate-700">
            Task names column
          </Label>
          <Switch
            id="gantt-tasklist"
            checked={doc.view.showTaskList}
            onCheckedChange={(on) => setView({ showTaskList: on })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="gantt-links" className="text-sm text-slate-700">
            Dependency arrows
          </Label>
          <Switch
            id="gantt-links"
            checked={doc.view.showLinks}
            onCheckedChange={(on) => setView({ showLinks: on })}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Chart colours
          </Label>
          <p className="text-xs text-slate-400">Bars</p>
          <SwatchRow
            swatches={swatches}
            value={doc.view.baseColorName}
            onPick={(name) => setView({ baseColorName: name })}
            inheritLabel="Deck accent"
            inheritColor="var(--scs-gantt-accent, #37a9ef)"
          />
          <p className="text-xs text-slate-400">Completed</p>
          <SwatchRow
            swatches={swatches}
            value={doc.view.doneColorName}
            onPick={(name) => setView({ doneColorName: name })}
            inheritLabel="Darker bar"
            inheritColor="var(--scs-gantt-accent-strong, #1f6bd9)"
          />
          <p className="text-xs text-slate-400">Summary bars</p>
          <SwatchRow
            swatches={swatches}
            value={doc.view.summaryColorName}
            onPick={(name) => setView({ summaryColorName: name })}
            inheritLabel="Deck ink"
            inheritColor="var(--scs-gantt-summary, #64748b)"
          />
          <p className="text-xs text-slate-400">Summary completed</p>
          <SwatchRow
            swatches={swatches}
            value={doc.view.summaryDoneColorName}
            onPick={(name) => setView({ summaryDoneColorName: name })}
            inheritLabel="Darker summary"
            inheritColor="var(--scs-gantt-summary-strong, #334155)"
          />
          <p className="text-xs text-slate-400">Milestones</p>
          <SwatchRow
            swatches={swatches}
            value={doc.view.milestoneColorName}
            onPick={(name) => setView({ milestoneColorName: name })}
            inheritLabel="Darker bar"
            inheritColor="var(--scs-gantt-milestone, #1f6bd9)"
          />
        </div>

        {selectedTask && (
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              {selectedTask.text || "Selected task"}
            </Label>
            <p className="text-xs text-slate-400">Bar</p>
            <SwatchRow
              swatches={swatches}
              value={selectedTask.colorName || undefined}
              onPick={(name) => setTaskColor({ colorName: name ?? "" })}
              inheritLabel="Chart"
              inheritColor={
                swatchValue(swatches, doc.view.baseColorName) ??
                "var(--scs-gantt-accent, #37a9ef)"
              }
            />
            <p className="text-xs text-slate-400">Completed</p>
            <SwatchRow
              swatches={swatches}
              value={selectedTask.doneColorName || undefined}
              onPick={(name) => setTaskColor({ doneColorName: name ?? "" })}
              inheritLabel="Chart"
              inheritColor={
                swatchValue(swatches, doc.view.doneColorName) ??
                "var(--scs-gantt-accent-strong, #1f6bd9)"
              }
            />
          </div>
        )}

        {/* §26/§4.12 The render window. Optional, and absent is the normal case: the
            timeline is then derived from the schedule's own extent. Set, it is exactly what is
            asked for — "show me Q3" is a framing decision about a slide, not a change to the
            plan — and tasks outside it are clipped.

            Native date inputs rather than the deck's calendar popover: the value is an ISO day
            string on both sides, which is precisely what `<input type="date">` speaks, so there
            is no parsing or formatting layer to get wrong. */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Render window
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Render from"
              className="h-8 text-xs"
              value={doc.view.rangeStart ?? ""}
              onChange={(e) =>
                setView({ rangeStart: e.target.value || undefined })
              }
            />
            <span className="text-xs text-slate-400">→</span>
            <Input
              type="date"
              aria-label="Render to"
              className="h-8 text-xs"
              value={doc.view.rangeEnd ?? ""}
              onChange={(e) =>
                setView({ rangeEnd: e.target.value || undefined })
              }
            />
          </div>
          {doc.view.rangeStart || doc.view.rangeEnd ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-slate-500"
              onClick={() =>
                setView({ rangeStart: undefined, rangeEnd: undefined })
              }
            >
              Clear — fit to the schedule
            </Button>
          ) : (
            <p className="text-xs text-slate-400">
              Empty: the timeline follows the schedule, with a unit of air at
              each end.
            </p>
          )}
          {/* A start on or after its end is ignored rather than half-applied, so say so
              instead of drawing a chart with no columns and no explanation. */}
          {doc.view.rangeStart &&
          doc.view.rangeEnd &&
          doc.view.rangeStart >= doc.view.rangeEnd ? (
            <p className="text-xs text-amber-600">
              The start is not before the end, so the schedule&apos;s own range
              is being drawn.
            </p>
          ) : null}
        </div>

        {/* §4.3 Says out loud what "fixed" costs, because the failure is silent otherwise:
            a chart that no longer fits simply clips at the window edge. */}
        {doc.view.fit === "fixed" && (
          <p className="text-xs text-slate-400">
            Fixed cells keep the authored scale; a schedule wider than the
            window is clipped rather than scrolled.
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {api ? (
          <Suspense
            fallback={
              <div className="p-4 text-sm text-slate-400">
                Loading the task editor…
              </div>
            }
          >
            <GanttTaskEditor api={api} />
          </Suspense>
        ) : (
          <p className="p-4 text-sm text-slate-500">
            Select a task in the chart to edit its name, dates and progress
            here.
          </p>
        )}
      </div>
    </div>
  );
}
